/**
 * The type of the DocumentHiddenTrackerIds. Use this to ensure type safety for future changes
 */
export type DocumentHiddenTrackerId = number;

const documentHiddenTrackers = new Map<DocumentHiddenTrackerId, number>();

let visibilityChangeEventListenerAdded = false;
let trackerId: DocumentHiddenTrackerId = 0;

function onVisibilityChange() {
  if (document.hidden) {
    const currentTime = Date.now();
    documentHiddenTrackers.forEach((value, key) => {
      if (value === -1) {
        documentHiddenTrackers.set(key, currentTime);
      }
    });

    removeDocumentHiddenListener();
  }
}

function addDocumentHiddenListener() {
  visibilityChangeEventListenerAdded = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
}

function removeDocumentHiddenListener() {
  visibilityChangeEventListenerAdded = false;
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

function startTrackerForId(trackerId: DocumentHiddenTrackerId) {
  if (document.hidden) {
    documentHiddenTrackers.set(trackerId, 0);
  } else {
    visibilityChangeEventListenerAdded || addDocumentHiddenListener();
    documentHiddenTrackers.set(trackerId, -1);
  }
}

/**
 * Starts a document hidden tracker to measure if and when the document was ever hidden such as in a background tab.
 *
 * @returns a number that is used as a TrackerID in order to fetch the results of the tracker
 */
export function startDocumentHiddenTracker(): DocumentHiddenTrackerId {
  trackerId += 1;

  startTrackerForId(trackerId);

  return trackerId;
}

/**
 * Returns the status of a document hidden tracker for the specified trackerID.
 *
 * @param trackerId Id of the tracker, obtained from trackDocumentHidden.
 * @param unregister When set to true, it will unregister and delete the result from the tracker.
 *
 * @returns
 *  -1: The document was never hidden from the start of the measurement.
 *   0: Document was hidden from the start of the measurement.
 *  >0: The unix unix epoch time when the document was first hidden.
 */
export function getDocumentHiddenResult(trackerId: DocumentHiddenTrackerId, unregister?: boolean): number | undefined {
  const trackerValue = documentHiddenTrackers.get(trackerId);

  if (unregister) {
    unregisterDocumentHiddenTracker(trackerId);
  }

  return trackerValue;
}

/**
 * Removes a documentHidden tracker without fetching the value.
 *
 * @param trackerId Id of the tracker, obtained from trackDocumentHidden.
 */
export function unregisterDocumentHiddenTracker(trackerID: DocumentHiddenTrackerId): void {
  documentHiddenTrackers.delete(trackerID);
  if (documentHiddenTrackers.size === 0 && visibilityChangeEventListenerAdded) {
    removeDocumentHiddenListener();
  }
}
